import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getLocalidades } from "../../../../actions/forms";
import { geocodeAddress } from "../../../../actions/googleMaps";
import { removeAccentMarks, stringToTitleCase } from "../../../../utils/util";
import { useGoogleMapsApiKey } from "../../../commons/googleMaps/googleMapsKeyContext";
import GooglePlacesMap from "../../../commons/googleMaps/googlePlacesMap";
import FormikAutocomplete from "../../../commons/formik/formikAutocomplete";
import FormikTextField from "../../../commons/formik/formikTextField";
import { StyledCommonForms } from "../../../styled/form";
import FormSectionDivider from "../formSectionDivider";

const EMPTY_LOCALIDADES = { isFetching: false, didInvalidate: false, data: [] };

const getNestedValue = (object, path) =>
  path.split(".").reduce((current, key) => current?.[key], object);

const samePlaceName = (a, b) =>
  removeAccentMarks(String(a ?? ""))
    .trim()
    .toLowerCase() ===
  removeAccentMarks(String(b ?? ""))
    .trim()
    .toLowerCase();

const findCatalogOption = (options, raw) => {
  if (!raw || !options?.length) return undefined;
  return options.find(
    (option) =>
      samePlaceName(option.id, raw) || samePlaceName(option.nombre, raw),
  );
};

const buildAddressMapQuery = (values, namePrefix, isDesde) => {
  const donde = isDesde ? "Desde" : "Hasta";
  const read = (field) =>
    namePrefix
      ? getNestedValue(values, `${namePrefix}.${field}`)
      : values[`${field}${donde}`];
  const parts = [
    read("calle"),
    read("numero"),
    read("localidad"),
    read("provincia"),
    "Argentina",
  ]
    .map((part) => String(part ?? "").trim())
    .filter(Boolean);
  return parts.length >= 2 ? parts.join(", ") : "";
};

const DireccionFpt = ({
  isDesde,
  values,
  fields,
  disabled,
  setFieldValue,
  idArt,
  direccionesHabituales,
  setDireccionesHabitualesOtro,
  location,
  onLocationChange,
  onDireccionResolved,
  namePrefix,
  localidadesKey,
  title,
  showMap = true,
  showSearch = true,
  enableAutoGeocode = true,
  lockProvinciaLocalidad,
}) => {
  const dispatch = useDispatch();
  const { apiKey } = useGoogleMapsApiKey(idArt);
  const showGooglePlaces = showMap || showSearch;
  const disablePlaceFields = Boolean(
    disabled || (lockProvinciaLocalidad ?? showSearch),
  );
  const listaLocalidadesRedux = useSelector((state) => state.forms.localidades);

  const donde = isDesde ? "Desde" : "Hasta";
  const localidadSlot = localidadesKey || donde.toLowerCase();
  const catalogDonde = namePrefix ? "Desde" : donde;
  const nameOf = (field) =>
    namePrefix ? `${namePrefix}.${field}` : `${field}${donde}`;
  const nameEntreCalle1 = namePrefix
    ? `${namePrefix}.entreCalle1`
    : `entreCalle${donde}1`;
  const valueOf = (field) =>
    namePrefix ? getNestedValue(values, nameOf(field)) : values[nameOf(field)];
  const callesCapitalFederal = fields[`calle${catalogDonde}`].options;
  const localidadesSlot = listaLocalidadesRedux[localidadSlot] || EMPTY_LOCALIDADES;
  // Queda habilitado solo cuando ni el padrón ni Google trajeron código postal.
  const [codigoPostalEditable, setCodigoPostalEditable] = useState(false);

  const esCapitalFederal = valueOf("provincia") == "Capital Federal";

  const opcionesCalles = (callesSeleccionadas) => {
    const calles = {};

    // Evitamos repetir calles en las opciones
    callesCapitalFederal.forEach((calle) => {
      if (!calles[calle.nombre]) {
        calles[calle.nombre] = { id: calle.nombre, nombre: calle.nombre };
      }
    });
    const callesActuales = callesSeleccionadas
      .map((calle) => ({ id: calle, nombre: calle }))
      .filter((c) => c.id != "");
    const ret = [
      ...Object.keys(calles).map((calle) => calles[calle]),
      ...callesActuales,
    ];
    return ret;
  };
  // Siguiente calle guarda el valor de la calle hasta que se obtengan las nuevas calles (ya que asignar valor antes de opcion deja vacio el componente)
  const [siguienteCalle, setSiguienteCalle] = useState(null);
  const [opcionesCallesCapitalFederal, setOpcionesCallesCapitalFederal] =
    useState(
      opcionesCalles([
        valueOf("calle"),
        valueOf("entreCalle"),
        namePrefix ? getNestedValue(values, nameEntreCalle1) : values[nameEntreCalle1],
      ]),
    );

  const rawLocalidades = listaLocalidadesRedux[localidadSlot]?.data || [];
  const localidades = useMemo(() => {
    const seen = new Set();
    const list = [];
    rawLocalidades.forEach((lugar) => {
      const loc = String(lugar.localidad || "").trim();
      if (loc && !seen.has(loc.toLowerCase())) {
        seen.add(loc.toLowerCase());
        list.push({ id: loc, nombre: loc });
      }
    });
    return list;
  }, [rawLocalidades]);
  // Siguiente localidad guarda el valor de la localidad hasta que se obtengan nuevas localidades (ya que se deben buscar antes las opciones) Necesario para direcciones habituales/googlemaps
  const [siguienteLocalidad, setSiguienteLocalidad] = useState(null);
  const [ubicacionLocal, setUbicacionLocal] = useState(null);
  const ubicacion = location !== undefined ? location : ubicacionLocal;
  const syncMapWithFields = enableAutoGeocode || showMap;
  const mapAddressQuery = useMemo(
    () =>
      syncMapWithFields
        ? buildAddressMapQuery(values, namePrefix, isDesde)
        : "",
    [syncMapWithFields, values, namePrefix, isDesde],
  );
  const ubicacionRef = useRef(ubicacion);
  ubicacionRef.current = ubicacion;
  const sameLocation = (left, right) =>
    (left?.lat ?? null) === (right?.lat ?? null) &&
    (left?.lng ?? null) === (right?.lng ?? null);
  const setUbicacion = (value) => {
    if (sameLocation(ubicacionRef.current, value)) return;
    if (onLocationChange) onLocationChange(value);
    else setUbicacionLocal(value);
  };
  const setUbicacionRef = useRef(setUbicacion);
  setUbicacionRef.current = setUbicacion;
  // Si el mapa acaba de completar los campos, no geocodificar de vuelta
  // esa misma dirección (evita el ciclo mapa → form → mapa).
  const lastGeocodeQueryRef = useRef("");
  const skipFieldGeocodeRef = useRef(false);
  const geocodeQueryRef = useRef("");

  const buildGeocodeQuery = () => {
    const calle = valueOf("calle");
    const numero = valueOf("numero");
    const localidad = valueOf("localidad");
    const provincia = valueOf("provincia");
    if (!provincia || !localidad || !calle || !numero) return "";
    return [calle, numero, localidad, provincia, "Argentina"].join(", ");
  };
  geocodeQueryRef.current = buildGeocodeQuery();

  // Coordenadas ya guardadas: no geocodificar de nuevo al abrir el formulario.
  useEffect(() => {
    if (!enableAutoGeocode) return;
    const query = buildGeocodeQuery();
    if (!query) return;
    const loc = ubicacionRef.current;
    if (loc?.lat != null && loc?.lng != null) {
      lastGeocodeQueryRef.current = query;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setOpcionesCallesCapitalFederal(
      opcionesCalles([
        siguienteCalle ? siguienteCalle : valueOf("calle"),
        valueOf("entreCalle"),
        namePrefix ? getNestedValue(values, nameEntreCalle1) : values[nameEntreCalle1],
      ]),
    );
  }, [callesCapitalFederal, siguienteCalle]);

  // Caso direcciones habituales y googlePlacesAutocomplete. Esperar opciones antes de cambiar valor
  useEffect(() => {
    if (siguienteCalle !== null) {
      setFieldValue(nameOf("calle"), siguienteCalle);
      setSiguienteLocalidad(null);
    }
  }, [opcionesCallesCapitalFederal]);

  const localidadesRedux = listaLocalidadesRedux[localidadSlot] || EMPTY_LOCALIDADES;
  const localidadesDataRef = useRef(localidadesRedux.data);

  useEffect(() => {
    if (!localidadesKey || !idArt) return;
    const provincia = valueOf("provincia");
    if (!provincia || provincia === "Capital Federal") return;
    const slot = listaLocalidadesRedux[localidadesKey];
    if (slot?.data?.length || slot?.isFetching || slot?.didInvalidate) return;
    dispatch(getLocalidades(idArt, provincia, isDesde, localidadesKey));
  }, [localidadesKey, idArt]);

  // Caso direcciones habituales y googlePlacesAutocomplete. Esperar opciones antes de cambiar valor
  useEffect(() => {
    if (siguienteLocalidad == null) {
      return;
    }

    const targetLocalidad = String(siguienteLocalidad).trim();
    const localidadMatch = findCatalogOption(localidades, targetLocalidad);
    if (localidadMatch) {
      setFieldValue(nameOf("localidad"), localidadMatch.id);
      setSiguienteLocalidad(null);
      localidadesDataRef.current = localidadesRedux.data;
      return;
    }

    const dataChanged = localidadesRedux.data !== localidadesDataRef.current;
    if (dataChanged && !localidadesRedux.isFetching && localidades.length > 0) {
      localidadesDataRef.current = localidadesRedux.data;
      setFieldValue(nameOf("localidad"), targetLocalidad);
      setSiguienteLocalidad(null);
    }
  }, [localidades, siguienteLocalidad, localidadesRedux.data, localidadesRedux.isFetching]);

  // Actualizar codigo postal. El padrón gana; si no hay código, queda el de Google.
  useEffect(() => {
    const localidad = valueOf("localidad");
    const provincia = valueOf("provincia");
    const calle = valueOf("calle");
    const numero = valueOf("numero");
    const codigoPostalActual = valueOf("codigoPostal");

    if (!localidad || !provincia) {
      setCodigoPostalEditable(false);
      return;
    }

    const esCapital = provincia == "Capital Federal";
    let catalogCode;

    if (esCapital) {
      if (!calle || numero === "" || numero == null) {
        setCodigoPostalEditable(false);
        return;
      }
      catalogCode = callesCapitalFederal.find(
        (c) =>
          c.nombre == calle &&
          c.numeroDesde <= numero &&
          numero <= c.numeroHasta,
      )?.codigoPostal;
    } else {
      // La localidad arranca en Capital Federal hasta que llega el catálogo.
      if (localidad == "Capital Federal" || localidadesSlot.isFetching) return;
      catalogCode = (localidadesSlot.data || []).find(
        (p) => p.localidad == localidad,
      )?.codigoPostal;
    }

    const fromCatalog = catalogCode != null && catalogCode !== "";
    const nextCodigoPostal = fromCatalog ? catalogCode : codigoPostalActual;

    if (nextCodigoPostal !== codigoPostalActual) {
      setFieldValue(nameOf("codigoPostal"), nextCodigoPostal ?? "", false);
    }

    setCodigoPostalEditable((manual) => {
      if (fromCatalog) return false;
      if (manual) return true;
      return codigoPostalActual == null || codigoPostalActual === "";
    });
  }, [
    valueOf("localidad"),
    valueOf("provincia"),
    valueOf("calle"),
    valueOf("numero"),
    listaLocalidadesRedux.isFetching,
  ]);

  // Busqueda de Latitud y Longitud de la dirección ingresada por el usuario.
  useEffect(() => {
    if (!enableAutoGeocode || disabled || !apiKey) return;

    const query = geocodeQueryRef.current;

    // El mapa acaba de llenar los campos: no resolvemos esa misma
    // dirección otra vez. Consumimos el skip aunque la query esté vacía
    // para no perder el primer tipeo real.
    if (skipFieldGeocodeRef.current) {
      skipFieldGeocodeRef.current = false;
      if (query) lastGeocodeQueryRef.current = query;
      return;
    }

    if (!query || query === lastGeocodeQueryRef.current) return;


    const timeoutId = window.setTimeout(async () => {
      const latestQuery = geocodeQueryRef.current;
      if (!latestQuery || latestQuery === lastGeocodeQueryRef.current) return;

      try {
        // Places (New) por HTTP: no reescribe los campos, solo mueve el pin.
        const locationResult = await dispatch(geocodeAddress(apiKey, latestQuery));
        const location = locationResult?.value?.data;
        if (geocodeQueryRef.current !== latestQuery) return;
        if (!location) return;

        lastGeocodeQueryRef.current = latestQuery;
        setUbicacionRef.current(location);
      } catch {
        // Si Places no resuelve, el mapa queda donde estaba.
      }
    }, 1000);

    return () => window.clearTimeout(timeoutId);
  }, [
    enableAutoGeocode,
    disabled,
    apiKey,
    dispatch,
    valueOf("calle"),
    valueOf("numero"),
    valueOf("localidad"),
    valueOf("provincia"),
  ]);

  const setDireccionValue = (field, value) => {
    setFieldValue(field, value ? value : "");
  };

  const setDireccion = (value) => {
    // Solo salteamos el geocode de campos si el mapa/Places ya trajo
    // coordenadas. Las direcciones habituales no tienen location: hay
    // que buscarlas para mover el pin.
    skipFieldGeocodeRef.current = Boolean(value.location);
    setUbicacion(value.location || null);

    if (Object.keys(value).length != 0) {
      const localidad = removeAccentMarks(
        stringToTitleCase(value.localidad || ""),
      );
      const provincia = removeAccentMarks(
        stringToTitleCase(value.provincia || ""),
      );
      // Caso direccion guardada previamente localidad = Capital Federal y caso busqueda google
      if (
        localidad == "Capital Federal" ||
        provincia == "Ciudad Autonoma De Buenos Aires"
      ) {
        const capital = "Capital Federal";
        setDireccionValue(nameOf("provincia"), capital);
        setDireccionValue(nameOf("localidad"), capital);
        // Si tengo calles no las busco
        setSiguienteCalle(value.calle);
        setDireccionValue(nameOf("numero"), value.numero);
      } else {
        const provinciaMatch = findCatalogOption(
          fields[`provincia${catalogDonde}`].options,
          value.provincia,
        );
        const provinciaId = provinciaMatch?.id ?? value.provincia ?? "";
        setDireccionValue(nameOf("provincia"), provinciaId);
        const targetLoc = String(value.localidad || localidad || "").trim();
        setSiguienteLocalidad(targetLoc);
        if (provinciaMatch) {
          dispatch(getLocalidades(idArt, provinciaId, isDesde, localidadesKey));
        }
        setDireccionValue(nameOf("calle"), value.calle);
        setDireccionValue(nameOf("numero"), value.numero);
      }

      setDireccionValue(nameOf("codigoPostal"), value.codigoPostal);
      if (
        value.tipoDireccion !== null &&
        value.tipoDireccion !== undefined &&
        value.tipoDireccion !== ""
      ) {
        setDireccionValue(nameOf("tipoDireccion"), value.tipoDireccion);
      }
      onDireccionResolved?.(value);
    } else {
      setFieldValue(nameOf("provincia"), "");
      onProvinceChange("");
    }
  };

  const onLocalidadChange = (value) => {
    // Reiniciar los campos dependientes de localidad
    setFieldValue(nameOf("calle"), "");
    setFieldValue(nameOf("numero"), "");
    setFieldValue(nameOf("piso"), "");
    setFieldValue(nameOf("departamento"), "");
    setFieldValue(nameOf("entreCalle"), "");
    setFieldValue(nameEntreCalle1, "");
  };

  const onProvinceChange = (value) => {
    onLocalidadChange(null);
    if (value?.id == "Capital Federal") {
      setFieldValue(nameOf("localidad"), "Capital Federal");
    } else {
      setFieldValue(nameOf("localidad"), "");
      if (value?.id) {
        dispatch(getLocalidades(idArt, value.id, isDesde, localidadesKey));
      }
    }
  };

  return (
    <>
      {title !== "" && (
        <FormSectionDivider title={title || `Dirección ${donde}`} />
      )}
      <StyledCommonForms.Container>
        <FormikAutocomplete
          name={nameOf("direccionHabitual")}
          label="Direcciones habituales"
          variant="outlined"
          type="search"
          autoComplete="off"
          options={direccionesHabituales}
          callBack={(value) => {
            if (value) {
              setDireccion(value);

              setDireccionesHabitualesOtro((prevState) =>
                fields.direccionesHabituales.options.filter(
                  (d) => d.calle !== value.calle || d.tipoDireccion === 4,
                ),
              );
            } else {
              setDireccion({});
              setDireccionesHabitualesOtro([
                ...fields.direccionesHabituales.options,
              ]);
            }
          }}
          sx={{ ...StyledCommonForms.input, marginTop: 2 }}
          returnProp="calle"
          renderOption={(option) => {
            if (option.tipoDireccion === 4) {
              return "Otras";
            } else {
              return `${option.calle ? option.calle : ""} ${option.numero ? option.numero : ""
                } - ${option.localidad ? option.localidad : ""}, ${option.provincia ? option.provincia : ""
                }`;
            }
          }}
          filterOptions={(option) =>
            option.calle + option.numero + option.localidad + option.provincia
          }
          labelOption={(option) => {
            if (option.tipoDireccion === 4) {
              return "Otras";
            } else {
              return `${option.calle ? option.calle : ""} ${option.numero ? option.numero : ""
                } - ${option.localidad ? option.localidad : ""}, ${option.provincia ? option.provincia : ""
                }`;
            }
          }}
          disabled={disabled}
        />
      </StyledCommonForms.Container>
      {showGooglePlaces && (
        <GooglePlacesMap
          disabled={disabled}
          idArt={idArt}
          showSearch={showSearch}
          searches={[
            {
              key: namePrefix || donde,
              label: "Buscar una dirección para autocompletar los campos",
              title: title || `Dirección ${donde}`,
              location: syncMapWithFields ? ubicacion : undefined,
              query: mapAddressQuery,
              onPlaceSelected: setDireccion,
            },
          ]}
          showMap={showMap}
        />
      )}

      <StyledCommonForms.RowContainer>
        <StyledCommonForms.LeftColumnContainer>
          <FormikAutocomplete
            name={nameOf("provincia")}
            label={`Provincia ${fields[`provincia${catalogDonde}`].required ? "*" : ""
              }`}
            type="text"
            variant="outlined"
            options={fields[`provincia${catalogDonde}`].options}
            sx={StyledCommonForms.input}
            disabled={disablePlaceFields}
            callBack={(value) => {
              onProvinceChange(value, values);
            }}
          />
        </StyledCommonForms.LeftColumnContainer>
        <StyledCommonForms.RightColumnContainer>
          {valueOf("provincia") != "Capital Federal" ? (
            <FormikAutocomplete
              name={nameOf("localidad")}
              label={`Localidad ${fields[`localidad${catalogDonde}`].required ? "*" : ""
                }`}
              type="text"
              variant="outlined"
              sx={StyledCommonForms.input}
              options={localidades}
              disabled={disablePlaceFields || !valueOf("provincia")}
              callBack={(value) => {
                onLocalidadChange(value, values);
              }}
              getOptions={() => {
                dispatch(
                  getLocalidades(idArt, valueOf("provincia"), isDesde, localidadesKey),
                );
              }}
            />
          ) : (
            <FormikTextField
              name={nameOf("localidad")}
              label={`Localidad ${fields[`localidad${catalogDonde}`].required ? "*" : ""
                }`}
              type="text"
              variant="outlined"
              sx={StyledCommonForms.input}
              disabled={disablePlaceFields}
              callBack={(value) => {
                onLocalidadChange(value, values);
              }}
            />
          )}
        </StyledCommonForms.RightColumnContainer>
      </StyledCommonForms.RowContainer>

      <StyledCommonForms.RowContainer>
        <StyledCommonForms.LeftColumnContainer>
          {esCapitalFederal ? (
            <FormikAutocomplete
              name={nameOf("calle")}
              label={`Calle ${fields[`calle${catalogDonde}`].required ? "*" : ""}`}
              type="text"
              variant="outlined"
              sx={StyledCommonForms.input}
              disabled={disablePlaceFields || !valueOf("localidad")}
              options={opcionesCallesCapitalFederal}
              freeSolo
            />
          ) : (
            <FormikTextField
              name={nameOf("calle")}
              label={`Calle ${fields[`calle${catalogDonde}`].required ? "*" : ""}`}
              type="text"
              variant="outlined"
              sx={StyledCommonForms.input}
              disabled={disablePlaceFields || !valueOf("localidad")}
            />
          )}
        </StyledCommonForms.LeftColumnContainer>
        <StyledCommonForms.RightColumnContainer>
          <FormikTextField
            name={nameOf("numero")}
            label={`Número ${fields[`numero${catalogDonde}`].required ? "*" : ""}`}
            type="number"
            variant="outlined"
            sx={StyledCommonForms.input}
            disabled={disablePlaceFields || !valueOf("localidad")}
          />
        </StyledCommonForms.RightColumnContainer>
      </StyledCommonForms.RowContainer>
      <StyledCommonForms.RowContainer>
        <StyledCommonForms.LeftColumnContainer>
          <FormikTextField
            name={nameOf("piso")}
            label={`Piso ${fields[`piso${catalogDonde}`].required ? "*" : ""}`}
            type="text"
            variant="outlined"
            sx={StyledCommonForms.input}
            disabled={disabled || !valueOf("localidad")}
          />
        </StyledCommonForms.LeftColumnContainer>
        <StyledCommonForms.RightColumnContainer>
          <FormikTextField
            name={nameOf("departamento")}
            label={`Departamento ${fields[`departamento${catalogDonde}`].required ? "*" : ""
              }`}
            type="text"
            variant="outlined"
            sx={StyledCommonForms.input}
            disabled={disabled || !valueOf("localidad")}
          />
        </StyledCommonForms.RightColumnContainer>
      </StyledCommonForms.RowContainer>
      <StyledCommonForms.RowContainer>
        <StyledCommonForms.LeftColumnContainer>
          {esCapitalFederal ? (
            <FormikAutocomplete
              name={nameOf("entreCalle")}
              label={`Entre calle ${fields[`entreCalle${catalogDonde}`].required ? "*" : ""
                }`}
              type="text"
              variant="outlined"
              sx={StyledCommonForms.input}
              disabled={disabled || !valueOf("localidad")}
              options={opcionesCallesCapitalFederal}
              freeSolo
            />
          ) : (
            <FormikTextField
              name={nameOf("entreCalle")}
              label={`Entre calle ${fields[`entreCalle${catalogDonde}`].required ? "*" : ""
                }`}
              type="text"
              variant="outlined"
              sx={StyledCommonForms.input}
              disabled={disabled || !valueOf("localidad")}
            />
          )}
        </StyledCommonForms.LeftColumnContainer>
        <StyledCommonForms.RightColumnContainer>
          {esCapitalFederal ? (
            <FormikAutocomplete
              name={nameEntreCalle1}
              label={`Y calle ${fields[`entreCalle${catalogDonde}1`].required ? "*" : ""
                }`}
              type="text"
              variant="outlined"
              sx={StyledCommonForms.input}
              disabled={disabled || !valueOf("localidad")}
              options={opcionesCallesCapitalFederal}
              freeSolo
            />
          ) : (
            <FormikTextField
              name={nameEntreCalle1}
              label={`Y calle ${fields[`entreCalle${catalogDonde}1`].required ? "*" : ""
                }`}
              type="text"
              variant="outlined"
              sx={StyledCommonForms.input}
              disabled={disabled || !valueOf("localidad")}
            />
          )}
        </StyledCommonForms.RightColumnContainer>
      </StyledCommonForms.RowContainer>

      <StyledCommonForms.RowContainer>
        <StyledCommonForms.LeftColumnContainer>
          <FormikTextField
            name={nameOf("codigoPostal")}
            label="Código postal *"
            type="number"
            variant="outlined"
            sx={StyledCommonForms.input}
            disabled={disabled || !codigoPostalEditable}
          />
        </StyledCommonForms.LeftColumnContainer>
        <StyledCommonForms.RightColumnContainer>
          <FormikTextField
            name={nameOf("telefonos")}
            label={`Teléfono ${fields[`telefonos${catalogDonde}`].required ? "*" : ""
              }`}
            type="text"
            variant="outlined"
            sx={StyledCommonForms.input}
            disabled={disabled}
          />
        </StyledCommonForms.RightColumnContainer>
      </StyledCommonForms.RowContainer>
    </>
  );
};

export default DireccionFpt;
